import React from 'react';
import { connect } from 'react-redux';
import {
    EmptyStateSuggestionProps,
    mapDispatchToProps,
    mapStateToProps,
    mapTopDestinationToSuggestionItem,
} from 'component/common/TextSearch/TextSearchResult/EmptyStateSuggestion/EmptyStateSuggestion.selectors';
import { Box, Typography } from '@drone-js/core';
import SuggestionItemComponent from 'component/common/SuggestionItem/SuggestionItem';
import { getItemFromSessionStorage } from 'core-framework/window/storage';
import { SessionStorageKey } from 'core-framework/constant/StorageKeys';
import { EmptyStateItemType, SuggestionGroup, SuggestionItem } from 'core/type/SuggestionResult.types';
import { StringFormatter } from '@agoda/react-handyman';
import { MAX_RECENT_SEARCH, setRecentlySearch, transformSuggestionToRecentSearch } from 'component/common/TextSearch/suggestionUtils';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesEmptyStatePopularDestinationsContainerSeenEventActivitiesFieldsV1,
    ActivitiesEmptyStateTopActivitiesContainerSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesEmptyStatePopularDestinationsContainerSeenEventActivitiesManifestV1,
    ActivitiesEmptyStateTopActivitiesContainerSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { useInView } from 'react-intersection-observer';

const EmptyStateSuggestionComponent: React.FC<EmptyStateSuggestionProps> = (props) => {
    const suggestionGroupFromStorage = getItemFromSessionStorage(SessionStorageKey.TOP_ACTIVITIES);
    const suggestionGroup = suggestionGroupFromStorage ? (JSON.parse(suggestionGroupFromStorage) as SuggestionGroup) : null;
    const topActivitiesItems = suggestionGroup && suggestionGroup.items ? suggestionGroup.items : null;
    const isMobile = props.screenSize.mobile;
    const layoutDirection = isMobile ? 'column' : 'row';
    const maxItems = isMobile ? 4 : 8;
    const topActivitiesDisplay = topActivitiesItems && topActivitiesItems.length > 0 && topActivitiesItems.slice(0, maxItems);
    const topDestinationsDisplay = props.topDestinations.length > 0 && props.topDestinations.slice(0, maxItems);

    React.useEffect(() => {
        if (topActivitiesDisplay || topDestinationsDisplay) {
            props.trackEmptyStateSuggestionsSeen();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onTopActivitiesSeen = (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesEmptyStateTopActivitiesContainerSeenEventActivitiesFieldsV1>(ActivitiesEmptyStateTopActivitiesContainerSeenEventActivitiesManifestV1.ids, {});
    };

    const onPopularDestinationsSeen = (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesEmptyStatePopularDestinationsContainerSeenEventActivitiesFieldsV1>(
            ActivitiesEmptyStatePopularDestinationsContainerSeenEventActivitiesManifestV1.ids,
            {},
        );
    };

    const { ref: topActivitiesRef } = useInView({ triggerOnce: false, threshold: 0.25, onChange: onTopActivitiesSeen });
    const { ref: popularDestinationsRef } = useInView({ triggerOnce: false, threshold: 0.25, onChange: onPopularDestinationsSeen });

    if (!topActivitiesDisplay && !topDestinationsDisplay) {
        return null;
    }
    const handleSelect = (type: EmptyStateItemType, index: number, selected: SuggestionItem) => {
        return () => {
            const recentSearch = transformSuggestionToRecentSearch(selected, type);
            props.isSearchBoxDropdown && setRecentlySearch(recentSearch, MAX_RECENT_SEARCH);
            props.onEmptyStateSuggestionSelect(type, index + 1, selected.activityId, selected.cityId, selected.url, props.isActivityRedirectExperimentEnabled);
        };
    };

    return (
        <Box
            data-testid="activities-empty-state-suggestion-container"
            data-element-name="activities-empty-state-suggestion-container"
            display="flex"
            flexDirection={layoutDirection}
            p={isMobile || !props.isSearchBoxDropdown ? '12' : '0'}
            pt="12"
            maxWidth="744px"
            mx={!props.isSearchBoxDropdown ? 'auto' : '0'}
            width={!props.isSearchBoxDropdown ? '100%' : 'unset'}
        >
            {topActivitiesDisplay && (
                <Box ref={topActivitiesRef} display="flex" width="100%">
                    <Box
                        data-element-name="activities-empty-state-top-activities-container"
                        data-testid="activities-empty-state-top-activities-container"
                        display="flex"
                        flexDirection="column"
                        width="100%"
                        pb="12"
                    >
                        <Typography variant="body-default-strong" lineClamp={2} pb="8" ps="8">
                            {props.cms[310626]}
                        </Typography>

                        {topActivitiesDisplay.map((activity, index) => {
                            const subTitle = StringFormatter.format(`${props.cms[activity.categoryCms]} • ${activity.cityName}`);
                            return (
                                <Box mx={props.isSearchBoxDropdown ? '4' : '0'} key={`top_activities_${index}`}>
                                    <SuggestionItemComponent
                                        elementName="activities-empty-state-top-activities"
                                        title={activity.activityName}
                                        subTitle={subTitle}
                                        icon={activity.icon}
                                        onSelect={handleSelect(EmptyStateItemType.TopActivities, index, activity)}
                                        searchType={activity.type}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
            {topDestinationsDisplay && (
                <Box ref={popularDestinationsRef} display="flex" width="100%" ps={isMobile || !topActivitiesDisplay ? '0' : '8'}>
                    <Box
                        data-element-name="activities-empty-state-popular-destinations-container"
                        data-testid="activities-empty-state-popular-destinations-container"
                        display="flex"
                        flexDirection="column"
                        width="100%"
                        pb="12"
                    >
                        <Typography variant="body-default-strong" pb="8" ps="8">
                            {props.cms[310627]}
                        </Typography>

                        {topDestinationsDisplay.map((destination, index) => {
                            const suggestion = mapTopDestinationToSuggestionItem(destination);
                            return (
                                <Box mx={props.isSearchBoxDropdown ? '4' : '0'} key={`popular_destination_${index}`}>
                                    <SuggestionItemComponent
                                        elementName="activities-empty-state-popular-destinations"
                                        subTitle={destination.countryName || ''}
                                        title={destination.cityName}
                                        icon="fill.symbol.pin"
                                        onSelect={handleSelect(EmptyStateItemType.PopularDestinations, index, suggestion)}
                                        searchType={suggestion.type}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
EmptyStateSuggestionComponent.displayName = 'EmptyStateSuggestion';

const EmptyStateSuggestion = connect(mapStateToProps, mapDispatchToProps)(EmptyStateSuggestionComponent);
export default EmptyStateSuggestion;
